/**
 * Sentry browser SDK — initialized only when VITE_SENTRY_DSN is set at build time.
 * Release: import.meta.env.VITE_APP_RELEASE (see vite.config.ts).
 */

import * as Sentry from '@sentry/react';

import type { ErrorBoundaryReportContext } from './boundaryErrorContext';
import { isUnactionableClientNoise } from '../utils/isUnactionableClientNoise';
import { shouldDropSentryEvent } from '../utils/shouldDropSentryEvent';

let initialized = false;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || initialized) {
    return;
  }
  try {
    Sentry.init({
      dsn,
      environment: String(import.meta.env.VITE_ENV ?? import.meta.env.MODE ?? 'development'),
      release: import.meta.env.VITE_APP_RELEASE as string | undefined,
      sendDefaultPii: false,
      beforeSend(event, hint) {
        if (shouldDropSentryEvent(event, hint.originalException)) {
          return null;
        }
        return event;
      },
    });
    initialized = true;
  } catch (err) {
    // Do not set `initialized` — a later initSentry() call can retry (e.g. transient failure, tests).
    console.warn('Sentry initialization failed; app will run without Sentry', err);
  }
}

/** Sentry transport for boundary context — prefer `reportErrorFromBoundary` in UI. */
export function captureErrorToSentry(error: Error, context: ErrorBoundaryReportContext): void {
  if (!initialized) {
    return;
  }
  if (isUnactionableClientNoise(error)) {
    return;
  }
  Sentry.withScope((scope) => {
    scope.setTag('boundary', context.boundary);
    if (context.routeName) scope.setTag('route', String(context.routeName));
    if (context.featureName) scope.setTag('feature', String(context.featureName));
    if (context.errorId) scope.setExtra('errorId', String(context.errorId));
    if (context.componentStack != null && context.componentStack !== '') {
      scope.setExtra('componentStack', context.componentStack);
    }
    Sentry.captureException(error);
  });
}
